import { DrivingPage } from './driving/DrivingPage'
import { WalletPage } from './wallet/WalletPage'

/**
 * `manager-driving` — §9.6's driving surface, where the retired Expo app's work now happens.
 *
 * **A justified EXTRA in the parity diff.** Legacy has no `/driving` route; `route-diff.test.ts` requires
 * every extra to cite the upgrade it implements, and this one cites §9.6.
 *
 * The spec — including the web-capability mapping §9.6 asks be written *before* building — is
 * `docs/pages/manager-driving/driving-surface.md`. What that reading turned up is FB-180: legacy's
 * `driver.tsx` filtered drivers on a column that does not exist, matched orders on the wrong one, and had
 * no authentication at all. Its layout is ported; none of its wiring is.
 *
 * The guard mirrors the endpoints. `my-deliveries`, `my-availability` and `my-vehicle` are each
 * `@RequireAuth({"driver", "main_admin", "admin"}, alsoDrivers = true)`, so an administrator may open it and sees
 * their own board - empty unless they are marked *Jazdí aj rozvoz*, which is exactly what the server would give
 * them, and the board says why (§9.759).
 */
export const managerDrivingModule = {
  name: 'manager-driving',
  routes: [
    {
      path: '/driving',
      page: 'driving',
      // Behind the auth gate, outside the chrome — §9.6 asks for fullscreen and phone-first, and a sidebar
      // on a phone in a driver's hand is neither.
      fullscreen: true,
      // §9.759, FB-1078 - what the three endpoints ask: the role Šofér, an administrator, or anybody the main
      // administrator marked *Jazdí aj rozvoz* (§9.686); a custom role counts as its base, as on the server.
      // §9.112 had put a right `driver` here that `PermissionCatalog.ALL` never held, so nobody could be given it,
      // it opened for the main administrator alone, and its note that nobody who could open the screen before
      // lost it was wrong the day it was written: every administrator did. The sidebar entry carries the same
      // guard, which `agos-nav.test.ts` holds equal.
      guard: { systemRoles: ['driver', 'main_admin', 'admin'], orAlsoDrives: true },
      component: DrivingPage,
    },
    {
      // §9.6's second surface: what I took, and what of it I hand over. Same guard as the wallet
      // endpoint, same fullscreen phone-first shape as the board it links back to.
      path: '/wallet',
      page: 'wallet',
      fullscreen: true,
      // FB-001: admit whoever the server lets read. `my-wallet` is
      // `@RequireAuth({"waiter","driver","main_admin","admin","cook"})` and its join is
      // `employee_id` for everyone who is not a driver — 2 813 orders, 48 504,22 €, all of waiters.
      // A wallet is not a driver-only thing, and guarding it as one would hide a waitress's own money
      // from her while the endpoint answered it happily.
      guard: { systemRoles: ['waiter', 'driver', 'main_admin', 'admin', 'cook'] },
      component: WalletPage,
    },
  ],
} as const

export { DrivingPage, WalletPage }
